/**
 * Client-side pcap (libpcap) file synthesis.
 *
 * Takes one or more log entries and produces a Blob containing
 * a valid pcap file with one record per log. Strategy per record (best to
 * worst fidelity):
 *
 *   1. log.packet / log.full_packet (full captured frame, base64)
 *      → wrapped as-is, Wireshark decodes Ethernet/L2 onwards.
 *   2. log.payload (L7 only) + log.src_ip / dest_ip / src_port / dest_port /
 *      proto → synthesise an IPv4 + TCP/UDP/ICMP header around the payload.
 *      Wireshark recognises every layer correctly even though the
 *      original headers were stripped (IP checksum is computed; L4
 *      checksums are left at 0 — flagged but parsed correctly).
 *   3. log.payload only, no usable metadata → raw IP frame, no protocol info.
 *
 * Everything happens in the browser: `atob`, `Uint8Array`, `DataView`, `Blob`,
 * `URL.createObjectURL`. No deps.
 */

const LINKTYPE_ETHERNET = 1;
const LINKTYPE_RAW      = 101;

const PROTO_NUM = { tcp: 6, udp: 17, icmp: 1 };


// ── base64 ────────────────────────────────────────────────────────────────
export const base64ToBytes = (b64) => {
  try {
    const binary = atob(String(b64).replace(/\s+/g, ""));
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
};


// ── link-layer heuristic ──────────────────────────────────────────────────
const guessLinkType = (bytes) => {
  // Bare IP packets start with version nibble 4 or 6. Ethernet starts with
  // a destination MAC, which almost never matches that pattern.
  if (bytes.length < 14) return LINKTYPE_RAW;
  const firstNibble = bytes[0] >> 4;
  return firstNibble === 4 || firstNibble === 6 ? LINKTYPE_RAW : LINKTYPE_ETHERNET;
};


// ── IPv4 + L4 header synthesis ────────────────────────────────────────────
const ipv4ToBytes = (ip) => {
  const parts = String(ip || "").split(".");
  if (parts.length !== 4) return null;
  const out = new Uint8Array(4);
  for (let i = 0; i < 4; i++) {
    const n = parseInt(parts[i], 10);
    if (!Number.isInteger(n) || n < 0 || n > 255) return null;
    out[i] = n;
  }
  return out;
};

const ipChecksum = (bytes) => {
  // RFC 1071 — 16-bit one's complement of the one's complement sum.
  let sum = 0;
  for (let i = 0; i < bytes.length; i += 2) {
    sum += (bytes[i] << 8) | bytes[i + 1];
  }
  while (sum >> 16) sum = (sum & 0xffff) + (sum >> 16);
  return (~sum) & 0xffff;
};

const synthesizeIpPacket = (log, payload) => {
  const proto = String(log.protocol || log.proto || "").toLowerCase();
  const protoNum = PROTO_NUM[proto];
  if (!protoNum) return null;

  const srcIp = ipv4ToBytes(log.src_ip || log.ipSource);
  const dstIp = ipv4ToBytes(log.dest_ip || log.ipDestination);
  if (!srcIp || !dstIp) return null;

  const srcPort = Number(log.src_port ?? log.SourcePort) | 0;
  const dstPort = Number(log.dest_port ?? log.DestinationPort) | 0;

  // ── L4 ──
  let l4;
  if (protoNum === 6) {
    // TCP, 20 bytes minimal — flags PSH|ACK so it looks like data with ack
    l4 = new Uint8Array(20);
    const v = new DataView(l4.buffer);
    v.setUint16(0, srcPort & 0xffff);
    v.setUint16(2, dstPort & 0xffff);
    l4[12] = 0x50;     // data offset = 5
    l4[13] = 0x18;     // PSH+ACK
    v.setUint16(14, 0xffff);  // window
  } else if (protoNum === 17) {
    // UDP, 8 bytes
    l4 = new Uint8Array(8);
    const v = new DataView(l4.buffer);
    v.setUint16(0, srcPort & 0xffff);
    v.setUint16(2, dstPort & 0xffff);
    v.setUint16(4, 8 + payload.length);   // length
  } else {
    // ICMP echo request
    l4 = new Uint8Array(8);
    l4[0] = 8;
  }

  // ── IPv4 ──
  const totalLen = 20 + l4.length + payload.length;
  const ip = new Uint8Array(20);
  const ipv = new DataView(ip.buffer);
  ip[0] = 0x45;                 // v4 + IHL 5
  ipv.setUint16(2, totalLen);
  ipv.setUint16(6, 0x4000);     // DF
  ip[8] = 64;                   // TTL
  ip[9] = protoNum;
  ip.set(srcIp, 12);
  ip.set(dstIp, 16);
  ipv.setUint16(10, ipChecksum(ip));

  const out = new Uint8Array(ip.length + l4.length + payload.length);
  out.set(ip, 0);
  out.set(l4, ip.length);
  out.set(payload, ip.length + l4.length);
  return out;
};


// Some consumers (e.g. aggregations' normalizeEvents) cherry-pick fields and
// keep the original Mongo doc under `raw`. Look there too so callers don't
// have to remember to re-expose `packet`/`payload`/`proto` themselves.
const pick = (log, key) => log?.[key] ?? log?.raw?.[key] ?? null;

// ── Per-log packet extraction (bytes + linktype) ──────────────────────────
const extractPacket = (log) => {
  const fullB64 = pick(log, "packet") || pick(log, "full_packet") || pick(log, "packet_full");
  if (fullB64) {
    const bytes = base64ToBytes(fullB64);
    if (bytes && bytes.length) {
      // The log may expose the original linktype on the document; prefer it
      // over our heuristic guess.
      const info = pick(log, "packet_info");
      const linktype = Number.isInteger(info?.linktype) ? info.linktype : guessLinkType(bytes);
      return { bytes, linktype };
    }
  }
  const payload = base64ToBytes(pick(log, "payload"));
  if (!payload || payload.length === 0) return null;

  // Build a virtual log view that prefers top-level fields but falls back
  // to `raw` for the IP/L4 synthesis metadata.
  const view = {
    src_ip:    pick(log, "src_ip")    || pick(log, "ipSource"),
    dest_ip:   pick(log, "dest_ip")   || pick(log, "ipDestination"),
    src_port:  pick(log, "src_port")  ?? pick(log, "SourcePort"),
    dest_port: pick(log, "dest_port") ?? pick(log, "DestinationPort"),
    protocol:  pick(log, "protocol"),
    proto:     pick(log, "proto"),
  };
  const synth = synthesizeIpPacket(view, payload);
  if (synth) return { bytes: synth, linktype: LINKTYPE_RAW };
  return { bytes: payload, linktype: LINKTYPE_RAW };
};


// ── Public API ────────────────────────────────────────────────────────────

/**
 * Build a 1-record pcap Blob from a single log. Returns null when neither
 * `packet` nor `payload` is available.
 */
export const buildPcap = (log, timestampMs) => buildPcapMulti([log], timestampMs);

/**
 * Build a multi-record pcap Blob from an array of logs. Records use each
 * log's own timestamp; logs without packet/payload data are skipped.
 * Mixed linktypes are tolerated by falling back to LINKTYPE_RAW.
 */
export const buildPcapMulti = (logs, fallbackTimestampMs) => {
  const records = [];
  let linktype = null;

  for (const log of (logs || [])) {
    const pkt = extractPacket(log);
    if (!pkt) continue;
    // First record dictates the file linktype. Subsequent records with a
    // different linktype get downgraded to RAW (IP only) when possible —
    // libpcap demands a single linktype per file.
    if (linktype === null) linktype = pkt.linktype;
    let bytes = pkt.bytes;
    if (pkt.linktype !== linktype) {
      // If we already committed to ETHERNET but this one is RAW, prepend
      // a fake ethernet header so it still parses.
      if (linktype === LINKTYPE_ETHERNET && pkt.linktype === LINKTYPE_RAW) {
        const eth = new Uint8Array(14);
        eth[12] = 0x08; eth[13] = 0x00; // EtherType IPv4
        const merged = new Uint8Array(eth.length + bytes.length);
        merged.set(eth, 0); merged.set(bytes, eth.length);
        bytes = merged;
      } else if (linktype === LINKTYPE_RAW && pkt.linktype === LINKTYPE_ETHERNET) {
        // Strip the 14-byte ethernet header.
        bytes = bytes.subarray(14);
      }
    }

    const tsMs =
      log.timestamp || log.timeStamp
        ? new Date(log.timestamp || log.timeStamp).getTime()
        : (Number.isFinite(fallbackTimestampMs) ? fallbackTimestampMs : Date.now());

    records.push({ bytes, tsMs });
  }

  if (records.length === 0) return null;
  if (linktype === null) linktype = LINKTYPE_RAW;

  // Compute total size
  const totalSize = 24 + records.reduce((acc, r) => acc + 16 + r.bytes.length, 0);
  const buf = new ArrayBuffer(totalSize);
  const view = new DataView(buf);

  // Global header (LE)
  view.setUint32(0,  0xa1b2c3d4, true);
  view.setUint16(4,  2, true);
  view.setUint16(6,  4, true);
  view.setInt32 (8,  0, true);
  view.setUint32(12, 0, true);
  view.setUint32(16, 65535, true);
  view.setUint32(20, linktype, true);

  let offset = 24;
  for (const r of records) {
    const tsSec = Math.floor(r.tsMs / 1000);
    const tsUsec = (r.tsMs % 1000) * 1000;
    view.setUint32(offset,     tsSec, true);
    view.setUint32(offset + 4, tsUsec, true);
    view.setUint32(offset + 8, r.bytes.length, true);   // incl_len
    view.setUint32(offset + 12, r.bytes.length, true);  // orig_len
    new Uint8Array(buf, offset + 16, r.bytes.length).set(r.bytes);
    offset += 16 + r.bytes.length;
  }

  return new Blob([buf], { type: "application/vnd.tcpdump.pcap" });
};

// ════════════════════════════════════════════════════════════════════════════
// PcapNG with per-packet comments — Wireshark shows them in the
// "Packet comments" pane and "Edit › Packet Comment". Far more useful than
// classic pcap when bundling many packets, since each packet keeps the
// detection that flagged it.
// ════════════════════════════════════════════════════════════════════════════

const BLOCK_SHB = 0x0a0d0d0a;     // Section Header Block
const BLOCK_IDB = 0x00000001;     // Interface Description Block
const BLOCK_EPB = 0x00000006;     // Enhanced Packet Block
const BYTE_ORDER_MAGIC = 0x1a2b3c4d;
const OPT_END_OF_OPT = 0;
const OPT_COMMENT    = 1;
const PAD = (n) => (4 - (n & 3)) & 3;

const utf8Encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
const encodeUtf8 = (s) =>
  utf8Encoder ? utf8Encoder.encode(s) : new Uint8Array([...unescape(encodeURIComponent(s))].map(c => c.charCodeAt(0)));

const buildOption = (code, valueBytes) => {
  // Option layout: u16 code | u16 length | bytes | padding-to-32-bit
  const padLen = PAD(valueBytes.length);
  const out = new Uint8Array(4 + valueBytes.length + padLen);
  const v = new DataView(out.buffer);
  v.setUint16(0, code, true);
  v.setUint16(2, valueBytes.length, true);
  out.set(valueBytes, 4);
  return out;
};

const concatBytes = (...chunks) => {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
};

const buildBlock = (type, body) => {
  // Block layout: u32 type | u32 total_length | body | u32 total_length
  const totalLen = 12 + body.length;
  const out = new Uint8Array(totalLen);
  const v = new DataView(out.buffer);
  v.setUint32(0, type, true);
  v.setUint32(4, totalLen, true);
  out.set(body, 8);
  v.setUint32(totalLen - 4, totalLen, true);
  return out;
};

const buildSHB = () => {
  // section_length = -1 (unknown). Use a Uint8Array since DataView setBig*
  // isn't universally available — write 0xFFFFFFFFFFFFFFFF directly.
  const body = new Uint8Array(16);
  const v = new DataView(body.buffer);
  v.setUint32(0, BYTE_ORDER_MAGIC, true);
  v.setUint16(4, 1, true);                  // major version
  v.setUint16(6, 0, true);                  // minor version
  for (let i = 8; i < 16; i++) body[i] = 0xff;  // section_length = -1
  return buildBlock(BLOCK_SHB, body);
};

const buildIDB = (linktype, snaplen = 65535) => {
  // Body: u16 linktype | u16 reserved | u32 snaplen
  const body = new Uint8Array(8);
  const v = new DataView(body.buffer);
  v.setUint16(0, linktype, true);
  v.setUint16(2, 0, true);
  v.setUint32(4, snaplen, true);
  return buildBlock(BLOCK_IDB, body);
};

const buildEPB = (interfaceId, tsMs, packetBytes, comment) => {
  // tsMs → tsHigh:tsLow at microsecond resolution.
  // We avoid BigInt (not in all lint configs) — JS Numbers are safe
  // integers up to 2^53, and ms-to-µs of any realistic date stays well
  // under that. Split into two 32-bit unsigned halves manually.
  const usec = Math.floor(tsMs * 1000);
  const tsHigh = Math.floor(usec / 0x100000000);
  const tsLow  = (usec - tsHigh * 0x100000000) >>> 0;

  // Base fixed header (20 bytes) + packet data (padded) + options
  const padPkt = PAD(packetBytes.length);
  let optionsBytes = new Uint8Array(0);
  if (comment) {
    const commentBytes = encodeUtf8(comment);
    optionsBytes = concatBytes(
      buildOption(OPT_COMMENT, commentBytes),
      buildOption(OPT_END_OF_OPT, new Uint8Array(0)),
    );
  }

  const body = new Uint8Array(20 + packetBytes.length + padPkt + optionsBytes.length);
  const v = new DataView(body.buffer);
  v.setUint32(0,  interfaceId, true);
  v.setUint32(4,  tsHigh, true);
  v.setUint32(8,  tsLow, true);
  v.setUint32(12, packetBytes.length, true);   // captured length
  v.setUint32(16, packetBytes.length, true);   // original length
  body.set(packetBytes, 20);
  if (optionsBytes.length) body.set(optionsBytes, 20 + packetBytes.length + padPkt);

  return buildBlock(BLOCK_EPB, body);
};

/**
 * Build a pcap-NG Blob with one Enhanced Packet Block per log, each carrying
 * a Wireshark-visible comment with detection metadata (id, signature,
 * src→dst:port, category).
 *
 * Wireshark column to enable: "Comment" — or just open `Edit › Packet Comment`.
 *
 * Options:
 *   commentFor(log) → string : override the default comment
 *   sectionComment : string  : top-level section header comment
 */
export const buildPcapngMulti = (logs, fallbackTimestampMs, opts = {}) => {
  const records = [];
  let linktype = null;

  const defaultComment = (log) => {
    const lines = [];
    if (log.signature)     lines.push(String(log.signature));
    const id = log.signature_id || log.detection_id;
    if (id)                lines.push(`Detection ID: ${id}`);
    const sev = log.severityLabel || log.classificazione;
    if (sev)               lines.push(`Severity: ${sev}`);
    if (log.category)      lines.push(`Category: ${log.category}`);
    const proto = log.protocol || log.proto;
    const srcEp = log.src_ip ? `${log.src_ip}:${log.src_port ?? ""}` : null;
    const dstEp = log.dest_ip ? `${log.dest_ip}:${log.dest_port ?? ""}` : null;
    if (srcEp && dstEp)    lines.push(`${srcEp} → ${dstEp}${proto ? " · " + proto : ""}`);
    if (log.timestamp || log.timeStamp) {
      const when = new Date(log.timestamp || log.timeStamp);
      if (!isNaN(when.getTime())) {
        lines.push(`When: ${when.toISOString()}`);
      }
    }
    return lines.join("\n") || null;
  };

  for (const log of (logs || [])) {
    const pkt = extractPacket(log);
    if (!pkt) continue;
    if (linktype === null) linktype = pkt.linktype;
    let bytes = pkt.bytes;
    if (pkt.linktype !== linktype) {
      if (linktype === LINKTYPE_ETHERNET && pkt.linktype === LINKTYPE_RAW) {
        const eth = new Uint8Array(14);
        eth[12] = 0x08; eth[13] = 0x00;
        const merged = new Uint8Array(eth.length + bytes.length);
        merged.set(eth, 0); merged.set(bytes, eth.length);
        bytes = merged;
      } else if (linktype === LINKTYPE_RAW && pkt.linktype === LINKTYPE_ETHERNET) {
        bytes = bytes.subarray(14);
      }
    }
    const tsMs = log.timestamp || log.timeStamp
      ? new Date(log.timestamp || log.timeStamp).getTime()
      : (Number.isFinite(fallbackTimestampMs) ? fallbackTimestampMs : Date.now());

    const commentFn = opts.commentFor || defaultComment;
    const comment = commentFn(log);
    records.push({ bytes, tsMs, comment });
  }

  if (records.length === 0) return null;
  if (linktype === null) linktype = LINKTYPE_RAW;

  // Assemble: SHB → IDB → EPB × N
  const chunks = [buildSHB(), buildIDB(linktype)];
  for (const r of records) chunks.push(buildEPB(0, r.tsMs, r.bytes, r.comment));
  const final = concatBytes(...chunks);
  // Use a pcap-NG-specific MIME so browsers / OS download managers don't
  // helpfully "correct" the extension to .pcap based on the file type.
  return new Blob([final], { type: "application/x-pcapng" });
};

/** Convenience helper: trigger a browser download of a Blob. */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
